import React, { useState } from "react";
import { RefButton } from "../../like-button-ref/like-button-ref";
import { useFormik } from "formik";
import * as yup from "yup";
import ReactModal from "react-modal";
import classNames from "classnames";
import { ContactUsExtendedFormProps } from "./types";
import { Translate } from "../../../locales/locales-utils";
import { formatPhoneNumber } from "../../../helpers/formatPhoneNumber";
import { RequestDataType } from "../../../types/demoRequestTypes";
import { API_SERVER_URL } from "gatsby-env-variables-fork";
import { useBackendSettings } from "../../../layouts/useBackendSettings";

export const ContactUsExtendedForm = (props: ContactUsExtendedFormProps) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [needBrandingVal, setNeedBrandingVal] = useState("yes");
  const [needHostVal, setNeedHostVal] = useState("yes");
  const [eventFormatVal, setEventFormatVal] = useState("own-game");

  const backendSettings = useBackendSettings();
  const showBrandingAndHost = eventFormatVal !== "own-game";

  const validationSchema = (needConfirmUserAgreement: boolean, needConfirmPersonDataProcessing: boolean) => {
    return yup.object().shape({
      Phone: yup
        .string()
        .min(
          18,
          Translate(
            "Global.ContactUsExtendedForm.ItemPhoneValidationInvalidFormatMessage"
          )
        )
        .required(
          Translate(
            "Global.ContactUsExtendedForm.ItemPhoneValidationRequiredMessage"
          )
        ),
      AgreementPolicy: yup.bool().oneOf([true]),
      UserAgreements: needConfirmUserAgreement
        ? yup.bool().oneOf([true])
        : yup.boolean(),
      PersonDataProcessing: needConfirmPersonDataProcessing
        ? yup.bool().oneOf([true])
        : yup.boolean(),
    });
  };

  const form = useFormik({
    initialValues: {
      EmailOrTelegram: "",
      Phone: "",
      NeedBranding: "yes",
      NeedHost: "yes",
      EventFormat: "own-game",
      AgreementPolicy: false,
      UserAgreements: false,
      PersonDataProcessing: false
    },
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: validationSchema(
      backendSettings?.needConfirmUserAgreement ?? false,
      backendSettings?.needConfirmPersonDataProcessing ?? false
    ),
    onSubmit: async (values) => {
      const response = await fetch(`${API_SERVER_URL}/api/demo/add`, {
        credentials: "include",
        referrer: "/",
        method: "POST",
        cache: "no-cache",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify({
          UseTo: "Landings",
          Phone: values.Phone || "",
          AcceptedPrivacy: values.AgreementPolicy || false,
          AcceptedUserAgreement: values.UserAgreements || false,
          AcceptedPersonDataProcessing: values.PersonDataProcessing || false,
          Type: RequestDataType.ContactUs,
          RequestURL: window.location.href,
          Data:
            `${
              values.EmailOrTelegram
                ? "Email or telegram: " + values.EmailOrTelegram + " | "
                : ""
            }` +
            `${
              values.EventFormat !== "own-game" && values.NeedBranding
                ? "Need branding: " +
                values.NeedBranding.charAt(0).toUpperCase() +
                values.NeedBranding.slice(1) +
                " | "
                : ""
            }` +
            `${
              values.EventFormat !== "own-game" && values.NeedHost
                ? "Need a host: " +
                values.NeedHost.charAt(0).toUpperCase() +
                values.NeedHost.slice(1) +
                " | "
                : ""
            }` +
            `${
              values.EventFormat
                ? "Event format: " +
                eventFormatSelectOptions.find(
                  (option) => option.value === values.EventFormat
                )?.name
                : ""
            }`,
        }),
      });
      if (response.ok) setShowSuccessMessage(true);
    },
  });

  const yesOrNoSelectOptions = [
    {
      name: Translate("Global.ContactUsExtendedForm.ItemYes"),
      value: "yes",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemNo"),
      value: "no",
    },
  ];

  const eventFormatSelectOptions = [
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.OwnGame"),
      value: "own-game",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.Kinescope"),
      value: "kinescope",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.Vk"),
      value: "vk",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.Telegram"),
      value: "telegram",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.Zoom"),
      value: "zoom",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.Twitch"),
      value: "twitch",
    },
    {
      name: Translate(
        "Global.ContactUsExtendedForm.ItemEventsFormat.YandexTelemost"
      ),
      value: "yandex-telemost",
    },
    {
      name: Translate("Global.ContactUsExtendedForm.ItemEventsFormat.Other"),
      value: "other",
    },
  ];

  const closeModal = () => {
    props.closeHandle();
    setShowSuccessMessage(false);
    setNeedBrandingVal("yes");
    setNeedHostVal("yes");
    setEventFormatVal("own-game");
    form.resetForm();
  };

  return (
    <ReactModal
      isOpen={props.isOpen}
      bodyOpenClassName="modalWhiteBg modalForm"
    >
      <button className={"modalCloseBtn"} onClick={closeModal}></button>
      <form
        onSubmit={form.handleSubmit}
        noValidate
        className={showSuccessMessage ? "formWasSend" : ""}
      >
        {showSuccessMessage && (
          <div className="formMessage Success">
            <div className="formMessage-Icon">
              <svg
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="40" cy="40" r="40" fill="#65C15D" />
                <path
                  d="M30 38.3334L37.4274 46.5861C37.4665 46.6296 37.5344 46.6304 37.5745 46.5879L51.6667 31.6667"
                  stroke="white"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="h2 formMessageTitle">
              {Translate("Global.ContactUsExtendedForm.ItemSuccessTitle")}
            </div>
            <div className="h6 formMessageDescription">
              {Translate("Global.ContactUsExtendedForm.ItemSuccessText")}
            </div>
            <div className="formMessageActions">
              <RefButton
                text={Translate("Global.ContactUsForm.ItemSuccessButton")}
                variant="primary"
                size="lg"
                onClick={closeModal}
              />
            </div>
          </div>
        )}
        <div className="formHero">
          <div className="formHero-Title">
            {Translate("Global.ContactUsExtendedForm.ItemTitle")}
          </div>
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
              form.errors.EmailOrTelegram && form.touched.EmailOrTelegram,
            },
            {
              ["formControlBrandingSuccess"]:
              !form.errors.EmailOrTelegram && form.values.EmailOrTelegram,
            }
          )}
        >
          <label htmlFor="EmailOrTelegram">
            {Translate("Global.ContactUsExtendedForm.ItemEmailOrTelegram")}
          </label>
          <input
            type="text"
            id="EmailOrTelegram"
            name="EmailOrTelegram"
            placeholder={Translate(
              "Global.ContactUsExtendedForm.ItemEmailOrTelegramPlaceholder"
            )}
            value={form.values.EmailOrTelegram}
            onBlur={form.handleBlur}
            onChange={form.handleChange}
            onFocus={() =>
              form.setTouched({
                EmailOrTelegram: true,
                Phone: false,
                AgreementPolicy: false,
                UserAgreements: false,
              })
            }
          />
          {form.errors.EmailOrTelegram && form.touched.EmailOrTelegram && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.EmailOrTelegram}
            </div>
          )}
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
              form.errors.Phone && form.touched.Phone,
            },
            {
              ["formControlBrandingSuccess"]:
              !form.errors.Phone && form.values.Phone,
            }
          )}
        >
          <label htmlFor="Phone">
            {Translate("Global.ContactUsExtendedForm.ItemPhoneLabel")}
            <span className="required-star">*</span>
          </label>
          <input
            type="tel"
            id="Phone"
            name="Phone"
            placeholder={Translate(
              "Global.ContactUsExtendedForm.ItemPhonePlaceholder"
            )}
            value={form.values.Phone}
            onBlur={form.handleBlur}
            maxLength={19}
            onFocus={() =>
              form.setTouched({
                EmailOrTelegram: false,
                Phone: true,
                AgreementPolicy: false,
                UserAgreements: false,
              })
            }
            onChange={(e) => {
              form.setFieldValue(
                e.target.name,
                formatPhoneNumber(e.target.value, "+7 (000) 000-00-00")
              );
            }}
          />
          {form.errors.Phone && form.touched.Phone && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.Phone}
            </div>
          )}
        </div>
        <div className={classNames("formControl")}>
          <label htmlFor="EventFormat">
            {Translate("Global.ContactUsExtendedForm.ItemEventFormat")}
          </label>
          <select
            id="EventFormat"
            name="EventFormat"
            value={eventFormatVal}
            onChange={(e) => {
              setEventFormatVal(e.target.value);
              form.setFieldValue(e.target.name, e.target.value);
            }}
          >
            {eventFormatSelectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
        {showBrandingAndHost && (
          <>
            <div className={classNames("formControl")}>
              <label htmlFor="NeedBranding">
                {Translate("Global.ContactUsExtendedForm.ItemNeedBranding")}
              </label>
              <select
                id="NeedBranding"
                name="NeedBranding"
                value={needBrandingVal}
                onChange={(e) => {
                  setNeedBrandingVal(e.target.value);
                  form.setFieldValue(e.target.name, e.target.value);
                }}
              >
                {yesOrNoSelectOptions.map((option) => (
                  <option key={`nb_option_${option.value}`} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={classNames("formControl")}>
              <label htmlFor="NeedHost">
                {Translate("Global.ContactUsExtendedForm.ItemNeedHost")}
                <span className="required-star">*</span>
              </label>
              <select
                id="NeedHost"
                name="NeedHost"
                value={needHostVal}
                onChange={(e) => {
                  setNeedHostVal(e.target.value);
                  form.setFieldValue(e.target.name, e.target.value);
                }}
              >
                {yesOrNoSelectOptions.map((option) => (
                  <option key={`nh_option_${option.value}`} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="formAgreements">
          <div
            className={classNames("formAgreementsContainer", {
              ["error"]:
              form.errors.AgreementPolicy && form.touched.AgreementPolicy,
            })}
          >
            <input
              checked={form.values.AgreementPolicy}
              onChange={(event) => {
                const { checked } = event.target;
                form.setFieldValue("AgreementPolicy", checked);
                form.setTouched({
                  EmailOrTelegram: false,
                  Phone: false,
                  AgreementPolicy: true,
                  UserAgreements: false,
                });
              }}
              type="checkbox"
              name="AgreementPolicy"
              id="AgreementPolicy"
            />
            <label htmlFor="AgreementPolicy">
              {backendSettings?.needConfirmUserAgreement
                ? Translate(
                  "Global.ContactUsExtendedForm.ItemAgreementsPrivacyPolicy"
                )
                : Translate("Global.ContactUsExtendedForm.ItemAgreements")}
              <span className="required-star">*</span>
            </label>
          </div>
          {backendSettings?.needConfirmUserAgreement && (
            <div
              className={classNames("formAgreementsContainer", {
                ["error"]:
                form.errors.UserAgreements && form.touched.UserAgreements,
              })}
            >
              <input
                checked={form.values.UserAgreements}
                onChange={(event) => {
                  const { checked } = event.target;
                  form.setFieldValue("UserAgreements", checked);
                  form.setTouched({
                    EmailOrTelegram: false,
                    Phone: false,
                    AgreementPolicy: false,
                    UserAgreements: true,
                  });
                }}
                type="checkbox"
                name="UserAgreements"
                id="UserAgreements"
              />
              <label htmlFor="UserAgreements">
                {Translate(
                  "Global.ContactUsExtendedForm.ItemAgreementsUserAgreements"
                )}
                <span className="required-star">*</span>
              </label>
            </div>
          )}
           {backendSettings?.needConfirmPersonDataProcessing && (
            <div
              className={classNames("formAgreementsContainer", {
                ["error"]:
                form.errors.PersonDataProcessing && form.touched.PersonDataProcessing,
              })}
            >
              <input
                checked={form.values.PersonDataProcessing}
                onChange={(event) => {
                  const { checked } = event.target;
                  form.setFieldValue("PersonDataProcessing", checked);
                }}
                type="checkbox"
                name="PersonDataProcessing"
                id="PersonDataProcessing"
              />
              <label htmlFor="PersonDataProcessing">
                {Translate(
                  "Global.ContactUsExtendedForm.ItemAgreementsPersonDataProcessing"
                )}
                <span className="required-star">*</span>
              </label>
            </div>
          )}
        </div>
        <div className="formActionsBranding">
          <RefButton
            text={Translate("Global.ContactUsExtendedForm.ItemCancelButton")}
            variant="secondary"
            size="md"
            tag="button"
            onClick={closeModal}
          />
          <RefButton
            text={Translate("Global.ContactUsExtendedForm.ItemSubmitButton")}
            variant="primary"
            size="md"
            tag="button"
            type="submit"
          />
        </div>
      </form>
    </ReactModal>
  );
};
